/**
 * Inicio.
 *
 * El inicio muestra TRABAJO, no un índice de pantallas. El orden lo dice todo:
 *
 *  0. «Lo vendido» (`VentasDelMes`): cuánto se vendió este mes, el mes pasado y
 *     en el año, con cada número enlazado a las ventas que lo componen. Pedido
 *     del dueño: «un lugar donde se vean todas las ventas y el total del mes».
 *  1. «Próximos a vencer» —lo que el dueño pidió para levantar el teléfono— va
 *     arriba de todo. Es de otro agente y se monta tal cual (`ProximosPanel`).
 *  2. «Pendientes agendados»: lo que alguien anotó sobre un registro concreto
 *     desde el panel de seguimiento, de todos los módulos a la vez
 *     (`PendientesPanel`).
 *     Va antes que los contadores porque es trabajo con nombre y apellido —«no
 *     te olvides de llamar»— y no un número.
 *  3. «Tu trabajo de hoy»: acciones rápidas (emitir factura, cobrar…) y
 *     contadores reales de lo pendiente (`TrabajoHoy`).
 *  4. Recién al final, para el que quiere pasear, las áreas —agrupadas por
 *     frecuencia de uso y SIN el «N pantallas» de antes, que era presentarse
 *     diciendo cuántos botones se tienen—.
 *
 * Lo que se fue a propósito: el cartel «Hay 137 pantallas en 183 entradas de
 * menú» y la lista de «Módulos instalados». Eran el inventario del programador,
 * no el trabajo del usuario.
 */

import { Link } from 'react-router-dom';

import { ICONOS_DE_MENU, IconLayers } from '@/components/shell/icons';
import { menuPath } from '@/components/shell/menu-utils';
import { groupByTier, TIER_LABEL } from '@/components/shell/menu-tiers';
import { Card, EmptyState, Kbd, LoadingState, PageHeader } from '@/components/shell/primitives';
import { ProximosPanel } from '@/components/shell/ProximosPanel';
import { TrabajoHoy } from '@/components/shell/TrabajoHoy';
import { VentasDelMes } from '@/components/shell/VentasDelMes';
import { useMenusQuery } from '@/components/shell/queries';
import { PendientesPanel } from '@/views/PendientesPanel';
import { VentaRapidaLauncher } from '@/views/VentaRapidaLauncher';
import type { MenuNode } from '@/lib/types';
import { firstName, selectActiveCompany, useSessionStore } from '@/store/session';

/**
 * A dónde lleva un área: a su primera pantalla navegable. Un menú raíz casi
 * nunca tiene acción propia (es un contenedor), así que la tarjeta apunta a lo
 * primero que hay adentro —lo mismo que haría el usuario al desplegarlo—.
 */
function primeraPantalla(node: MenuNode): string | null {
  if (node.model !== null) return menuPath(node);
  for (const hijo of node.children) {
    const destino = primeraPantalla(hijo);
    if (destino !== null) return destino;
  }
  return null;
}

export function HomePage() {
  const user = useSessionStore((estado) => estado.user);
  const tenant = useSessionStore((estado) => estado.tenant);
  const empresa = useSessionStore(selectActiveCompany);
  const { data: menus, isPending } = useMenusQuery();

  const areas = menus ?? [];
  // El nombre de pila lo deduce el store: partir por el primer espacio saludaba
  // por el apellido y dejaba la coma colgando cuando el padrón guarda
  // «Apellido, Nombre».
  const nombre = firstName(user);
  const grupos = groupByTier(areas);

  return (
    <>
      <PageHeader
        title={nombre === '' ? 'Inicio' : `Hola, ${nombre}`}
        description={
          empresa === null
            ? tenant?.name ?? 'Nucleo ERP'
            : `Estás trabajando en ${empresa.name}${tenant === null ? '' : ` · ${tenant.name}`}.`
        }
      />

      {/* 1. Explorar las áreas, agrupadas por frecuencia de uso, sin contar botones.
          Va PRIMERO por pedido del dueño: lo primero que tiene que ver alguien que
          entra —sobre todo cuando le muestra el sistema a un cliente— es todo lo que
          el sistema hace. La agenda y los pendientes vienen después, porque son el
          trabajo del día de quien ya lo usa, no la carta de presentación. */}
      {isPending ? (
        <LoadingState label="Cargando tus accesos…" />
      ) : areas.length === 0 ? (
        <EmptyState
          title="Todavía no hay pantallas habilitadas"
          description="Tu usuario no tiene permisos de lectura sobre ningún modelo, o no hay módulos instalados en esta empresa."
        />
      ) : (
        <section aria-labelledby="areas-titulo" className="mt-6">
          <h2 id="areas-titulo" className="mb-3 text-sm font-semibold uppercase tracking-wide text-fg-faint">
            Explorar
          </h2>
          <div className="space-y-5">
            {grupos.map(({ tier, nodes }) => (
              <div key={tier}>
                <p className="mb-2 text-2xs font-semibold uppercase tracking-wide text-fg-faint">
                  {TIER_LABEL[tier]}
                </p>
                <ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {nodes.map((area) => {
                    const destino = primeraPantalla(area);
                    if (destino === null) return null;
                    const Icono =
                      area.icon !== null ? (ICONOS_DE_MENU[area.icon] ?? IconLayers) : IconLayers;
                    const atenuado = tier === 'config';
                    return (
                      <li key={area.name}>
                        <Link
                          to={destino}
                          className={
                            'group flex items-center gap-3 rounded-lg border border-line bg-surface px-3.5 py-2.5 transition-colors hover:border-accent/40 hover:bg-surface-raised ' +
                            (atenuado ? 'text-fg-muted' : '')
                          }
                        >
                          <span
                            className={
                              'grid h-8 w-8 shrink-0 place-items-center rounded-md ' +
                              (atenuado
                                ? 'bg-surface-raised text-fg-faint'
                                : 'bg-accent-soft text-accent')
                            }
                          >
                            <Icono className="h-4 w-4" />
                          </span>
                          <span className="min-w-0 truncate text-sm font-medium">{area.label}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 2. Cuánto se vendió: el mes en curso en grande, el mes pasado y el año
          al lado para que ese número signifique algo. Va acá —arriba de la venta
          rápida y de la agenda— porque es lo que el dueño quiere ver al entrar:
          «el total de las ventas que se vienen llevando a cabo». Cada número es
          un enlace a las ventas que lo componen. */}
      <VentasDelMes />

      {/* 3. Venta rápida: elegir un cliente y marcarle productos sin navegar a
          su ficha. Es el mismo selector que la solapa «Ventas» del contacto —un
          componente, dos puertas— y, como todo el flujo, NO factura nada. */}
      <VentaRapidaLauncher />

      {/* 4. Vencimientos: a quién hay que llamar hoy. */}
      <ProximosPanel />

      {/* 5. Lo agendado sobre registros concretos, de los trece módulos a la vez.
          Se agenda desde el panel «Seguimiento» de cualquier ficha y se cierra
          desde acá; la sección no se dibuja si no hay nada pendiente. */}
      <PendientesPanel />

      {/* 6. Acciones rápidas + pendientes reales. */}
      <TrabajoHoy />

      <Card className="mt-8 p-5">
        <h2 className="font-semibold">Navegación rápida</h2>
        <p className="mt-2 text-sm text-fg-muted">
          Abrí la paleta con <Kbd>Ctrl</Kbd> <Kbd>K</Kbd> (o <Kbd>⌘</Kbd> <Kbd>K</Kbd>) y escribí parte del
          nombre de una pantalla, una empresa o una acción. La barra lateral también tiene un filtro.
        </p>
      </Card>
    </>
  );
}
